import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import confetti from "@hiseb/confetti"
import { useNavigate } from "@tanstack/react-router"
import {
	IconCheckFill18 as CheckIcon,
	IconBellOnFill18,
	IconEnvelopeFill18,
	IconLink3Fill18,
	IconPaperPlane4Fill18 as PaperPlane,
} from "nucleo-ui-fill-18"
import {
	IconChevronDownOutline18 as ChevronDownIcon,
	IconCircleCheckOutline18 as CircleCheck,
	IconArrowDownToLineOutline18 as Download,
	IconSquareArrowUpRightOutline18 as SquareArrowUpRight,
	IconTriangleWarningOutline18 as TriangleWarning,
	IconUsersOutline18 as Users,
} from "nucleo-ui-outline-18"
import { CSVLink } from "react-csv"

import type {
	ChangelogActionButtonInput,
	ChangelogButtonMode,
} from "@productlane/lib/src/changelog-action-button"
import type { SegmentedControlOption } from "@productlane/ui"
import { SENDER_NAME_MAX_LENGTH } from "@productlane/api/src/lib/constants"
import { pro } from "@productlane/lib"
import {
	CHANGELOG_BUTTON_LABEL_MAX_LENGTH,
	CHANGELOG_BUTTON_URL_MESSAGES,
	checkChangelogButtonUrl,
	DEFAULT_CHANGELOG_BUTTON_LABEL,
	isOwnChangelogDomain,
	sanitizeChangelogButtonLabel,
} from "@productlane/lib/src/changelog-action-button"
import { cn } from "@productlane/lib/src/cn"
import { hasEnabledEmail } from "@productlane/lib/src/email-settings"
import {
	Button,
	Dialog,
	IconButton,
	Input,
	Loader,
	SegmentedControl,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"
import { Switch } from "@productlane/ui/src/components/form/switch/switch"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { SlackIcon } from "@productlane/ui/src/icons/slack-logo"
import { mutators } from "@productlane/zero/mutators"

import type { NotifyRecipientEntity } from "../notify-recipients/types"
import { SlackBroadcastChannelsPopover } from "@/components/shared/slack-broadcast-channels-popover"
import { toast } from "@/components/toast"
import { CHANGELOG_TITLE_MAXIMUM_LENGTH } from "@/constants"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { resolvePortalSenderName } from "@/lib/portal-sender-name"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	changelogPublishDate,
	changelogPublishFlag,
} from "@/screens/app/changelog/changelog-order"
import { useDebouncedValue } from "@/screens/settings/settings-pages/tool-policy/inputs/use-debounced-value"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { UpgradeButton } from "../../upgrade-button"
import { EmailPreviewFrame } from "./email-preview-frame"
import { changelogOwnDomains } from "./own-domains"
import { SlackMessagePreview } from "./slack-message-preview"

const COUNTDOWN_SECONDS = 10
const PREVIEW_DEBOUNCE_MS = 300

type PreviewTab = "email" | "slack"

const BUTTON_MODES = [
	{ value: "post", label: "Changelog post" },
	{ value: "custom", label: "Custom link" },
	{ value: "none", label: "No button" },
] as const satisfies readonly SegmentedControlOption<ChangelogButtonMode>[]

function pluralize(count: number, one: string, many: string) {
	return `${count.toLocaleString()} ${count === 1 ? one : many}`
}

function ChannelRow({
	title,
	meta,
	action,
}: {
	title: string
	meta: ReactNode
	action: ReactNode
}) {
	return (
		<div className="flex min-h-[3.25rem] items-center gap-3 border-t border-gray-200 py-2 first:border-t-0 dark:border-gray-700/80">
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="text-sm font-medium">{title}</span>
				<span className="flex min-w-0 items-center gap-1 text-xs text-secondary">
					{meta}
				</span>
			</div>
			<div className="flex flex-none items-center gap-2">{action}</div>
		</div>
	)
}

const FIELD_LABEL_CLASS = "w-24 flex-none pt-1.5 text-sm text-secondary"

// items-start keeps the label on the input's first line when an error or note grows the column.
function FieldRow({
	label,
	htmlFor,
	children,
}: {
	label: string
	htmlFor?: string
	children: ReactNode
}) {
	return (
		<div className="flex items-start gap-3 border-t border-gray-200 py-2.5 first:border-t-0 dark:border-gray-700/80">
			{htmlFor ? (
				<label htmlFor={htmlFor} className={FIELD_LABEL_CLASS}>
					{label}
				</label>
			) : (
				<span className={FIELD_LABEL_CLASS}>{label}</span>
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>
		</div>
	)
}

export function buildActionButton(
	mode: ChangelogButtonMode,
	label: string,
	url: string,
): ChangelogActionButtonInput {
	const text = sanitizeChangelogButtonLabel(label) || undefined
	if (mode === "none") return { mode }
	if (mode === "custom") return { mode, label: text, url: url.trim() }
	return { mode, label: text }
}

export function PublishChangelogDialog({
	isOpen,
	onOpenChange,
	entity,
}: {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	entity: NotifyRecipientEntity
}) {
	const navigate = useNavigate()
	const zero = useZero()
	const { passes: atLeastProPlan } = useRequiredPlans(pro.andUp)
	const workspace = useZeroWorkspace()
	const isSlackConfigured = !!workspace?.slackConnectSettings
	const isEmailConfigured = hasEnabledEmail(workspace?.emailSettings)
	const canUseEmail = isEmailConfigured && atLeastProPlan
	const canUseSlack = isSlackConfigured && atLeastProPlan
	const sendEmailByDefault =
		isEmailConfigured && workspace?.changelogEmailNotifications !== false

	const [sendEmail, setSendEmail] = useState(sendEmailByDefault)
	const [sendSlack, setSendSlack] = useState(isSlackConfigured)
	// Zero hydrates the workspace after the first render, so the switches follow
	// the resolved settings instead of the mount-time snapshot.
	useEffect(() => setSendEmail(sendEmailByDefault), [sendEmailByDefault])
	useEffect(() => setSendSlack(isSlackConfigured), [isSlackConfigured])

	// Every live address can send a broadcast; the broadcast default comes first.
	const fromOptions = useMemo(
		() =>
			(workspace?.emailSettings ?? [])
				.filter((address) => address.isEnabled)
				.map((address) => ({
					email: address.primaryEmail,
					isBroadcastDefault: address.isBroadcastDefault,
					isPrimary: address.isPrimary,
				}))
				.sort((a, b) => {
					if (a.isBroadcastDefault !== b.isBroadcastDefault)
						return a.isBroadcastDefault ? -1 : 1
					if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
					return a.email.localeCompare(b.email)
				}),
		[workspace?.emailSettings],
	)
	const [fromOverride, setFromOverride] = useState<string | null>(null)
	const fromEmail =
		fromOverride && fromOptions.some((option) => option.email === fromOverride)
			? fromOverride
			: fromOptions[0]?.email

	// Content on a named portal is sent under that portal's name.
	const defaultSenderName = useMemo(
		() =>
			resolvePortalSenderName({
				portalInstanceId: entity.portalInstanceId,
				portalInstances: workspace?.portalInstances,
				workspaceName: workspace?.name,
			}),
		[entity.portalInstanceId, workspace?.portalInstances, workspace?.name],
	)

	const [subject, setSubject] = useState("")
	const [senderName, setSenderName] = useState("")
	const [buttonMode, setButtonMode] = useState<ChangelogButtonMode>("post")
	const [buttonLabel, setButtonLabel] = useState("")
	const [buttonUrl, setButtonUrl] = useState("")
	const [previewTab, setPreviewTab] = useState<PreviewTab>("email")
	const [isSending, setIsSending] = useState(false)

	useEffect(() => {
		if (!isOpen) return
		setSubject(`New: ${entity.name}`)
		setSenderName(defaultSenderName)
		setFromOverride(null)
		setButtonMode("post")
		setButtonLabel("")
		setButtonUrl("")
	}, [isOpen, entity.name, defaultSenderName])

	const ownDomains = useMemo(() => changelogOwnDomains(workspace), [workspace])
	const linkCheck = checkChangelogButtonUrl(buttonUrl)
	const linkBlocked = buttonMode === "custom" && !linkCheck.ok
	const actionButton = buildActionButton(buttonMode, buttonLabel, buttonUrl)

	const emailOn = sendEmail && canUseEmail
	const slackOn = sendSlack && canUseSlack
	const tabs = [
		...(emailOn ? [{ value: "email" as const, label: "Email" }] : []),
		...(slackOn ? [{ value: "slack" as const, label: "Slack" }] : []),
	]
	const activeTab = tabs.some((tab) => tab.value === previewTab)
		? previewTab
		: tabs[0]?.value

	// A link still being typed is never sent to the server; its button text still previews.
	const previewKey = JSON.stringify({
		changelogId: entity.id,
		actionButton: linkBlocked
			? {
					mode: "post",
					label: actionButton.mode === "none" ? undefined : actionButton.label,
				}
			: actionButton,
		senderName: senderName.trim() || undefined,
		subject: subject.trim() || undefined,
		fromEmail: fromEmail || undefined,
	})
	const settledPreviewKey = useDebouncedValue(previewKey, PREVIEW_DEBOUNCE_MS)
	const previewQuery = api.mail.previewChangelogBroadcast.useQuery(
		JSON.parse(settledPreviewKey) as Parameters<
			typeof api.mail.previewChangelogBroadcast.useQuery
		>[0],
		{
			enabled: isOpen && !!activeTab,
			keepPreviousData: true,
			staleTime: 30_000,
		},
	)
	const preview = previewQuery.data

	const subscribersQuery =
		api.mail.getChangelogSubscribedContactsCount.useQuery(undefined, {
			enabled: isOpen,
		})
	const subscriberCount = subscribersQuery.data ?? 0
	const subscriberEmailsQuery =
		api.mail.getChangelogSubscribedContactsEmails.useQuery(undefined, {
			enabled: isOpen && canUseEmail,
		})
	const csvData = useMemo(
		() =>
			[
				["email"],
				...(subscriberEmailsQuery.data ?? []).map((email) => [email]),
			] as [["email"], ...[string][]],
		[subscriberEmailsQuery.data],
	)
	const slackChannelsQuery = api.mail.getSlackChannelsForBroadcast.useQuery(
		undefined,
		{ enabled: isOpen },
	)
	const slackChannels = slackChannelsQuery.data?.channels ?? []
	const slackChannelCount = slackChannelsQuery.data?.count ?? 0

	const sendBulkUpdateMutation = api.mail.sendBulkUpdate.useMutation()
	const undoBulkUpdateMutation = api.mail.undoBulkUpdate.useMutation()
	const triggerSlackMutation = api.mail.triggerSlackBroadcast.useMutation()
	const testEmailMutation = api.mail.sendTestUpdate.useMutation({
		onError: (error) =>
			toast.error({
				title: "Failed to send test email",
				description: error.message,
			}),
	})
	const testSlackMutation = api.mail.sendTestChangelogSlack.useMutation({
		onError: (error) =>
			toast.error({
				title: "Failed to send test Slack message",
				description: error.message,
			}),
	})

	const explainBlockedLink = useCallback(() => {
		if (linkCheck.ok) return
		toast.info({
			id: "changelog-button-link",
			title: "Fix the button link first",
			description: CHANGELOG_BUTTON_URL_MESSAGES[linkCheck.problem],
			icon: IconLink3Fill18,
		})
	}, [linkCheck])

	const explainAlwaysOn = () => {
		toast.info({
			id: "changelog-in-app-always-on",
			title: "In-app notifications are always on",
			description: "Turn changelog notifications off in Widget settings.",
			icon: IconBellOnFill18,
			action: {
				label: "Widget settings",
				callback: () => void navigate({ to: "/settings/widget" }),
			},
		})
	}

	const handleUndo = useCallback(async () => {
		try {
			await undoBulkUpdateMutation.mutateAsync({
				entityId: entity.id,
				entityType: entity.type,
			})
			toast.info({
				title: "Send cancelled",
				description: "The notifications was not sent.",
			})
		} catch (error) {
			toast.error({
				title: "Failed to cancel",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		}
	}, [entity.id, entity.type, undoBulkUpdateMutation])

	const executeSend = async () => {
		if (linkBlocked) {
			explainBlockedLink()
			return
		}
		try {
			setIsSending(true)
			await mutateOk(
				zero.mutate(
					mutators.changelogs.update(changelogPublishDate(entity.id)),
				),
			)
			// The backend waits 10 seconds before sending, so Undo can still cancel.
			if (emailOn) {
				await sendBulkUpdateMutation.mutateAsync({
					entityType: entity.type,
					entityName: entity.name,
					entityId: entity.id,
					senderName: senderName.trim() || undefined,
					fromEmail: fromEmail || undefined,
					subject: subject.trim() || undefined,
					actionButton,
				})
			}
			if (slackOn) {
				await triggerSlackMutation.mutateAsync({
					changelogId: entity.id,
					actionButton,
				})
			}
			await mutateOk(
				zero.mutate(
					mutators.changelogs.update(changelogPublishFlag(entity.id)),
				),
			)

			const positions = [
				{ x: window.innerWidth * 0.5, y: window.innerHeight * 0.6 },
				{ x: window.innerWidth * 0.25, y: window.innerHeight * 0.45 },
				{ x: window.innerWidth * 0.75, y: window.innerHeight * 0.4 },
			]
			positions.forEach((position, i) =>
				setTimeout(() => confetti({ position }), i * 200),
			)

			const parts = [
				...(emailOn && subscriberCount > 0
					? [pluralize(subscriberCount, "subscriber", "subscribers")]
					: []),
				...(slackOn
					? [pluralize(slackChannelCount, "Slack channel", "Slack channels")]
					: []),
			]
			if (parts.length > 0) {
				toast.info({
					title: "Changelog published",
					description: `Notifications will be sent to ${parts.join(" and ")} in ${COUNTDOWN_SECONDS} seconds`,
					action: { label: "Undo", callback: handleUndo },
					time: COUNTDOWN_SECONDS * 1000,
					showProgress: true,
				})
			} else {
				toast.success({
					title: "Changelog published",
					description: "Published without notifications.",
				})
			}
			onOpenChange(false)
		} catch (error) {
			toast.error({
				title: "Failed to send update",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		} finally {
			setIsSending(false)
		}
	}

	const handleSendTest = () => {
		if (linkBlocked) {
			explainBlockedLink()
			return
		}
		if (activeTab === "slack") {
			testSlackMutation.mutate(
				{ changelogId: entity.id, actionButton },
				{
					onSuccess: ({ channelName }) =>
						toast.success({
							title: "Test changelog posted",
							description: `Check #${channelName} in Slack.`,
							icon: SlackIcon,
						}),
				},
			)
			return
		}
		testEmailMutation.mutate(
			{
				entityType: entity.type,
				entityId: entity.id,
				entityName: entity.name,
				senderName: senderName.trim() || undefined,
				fromEmail: fromEmail || undefined,
				subject: subject.trim() || undefined,
				actionButton,
			},
			{
				onSuccess: ({ to }) =>
					toast.success({
						title: "Test changelog sent",
						description: `Check your inbox at ${to}.`,
						icon: IconEnvelopeFill18,
					}),
			},
		)
	}
	const isSendingTest =
		testEmailMutation.isLoading || testSlackMutation.isLoading

	const summaryParts = [
		...(emailOn
			? [pluralize(subscriberCount, "subscriber", "subscribers")]
			: []),
		...(slackOn
			? [pluralize(slackChannelCount, "Slack channel", "Slack channels")]
			: []),
	]

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={<span className="block px-5 pt-5">Publish changelog</span>}
			description={
				<span className="block truncate px-5 pr-14">{entity.name}</span>
			}
			size="xl"
			darkOverlay
			className="!max-w-[min(75rem,96vw)]"
			contentPadding="p-0"
			scrollContainerClasses="mx-0 px-0 py-0 overflow-hidden"
			footerClassName="mt-0 border-t border-gray-200 px-5 py-3 sm:justify-between dark:border-gray-700/80"
			onOpenAutoFocus={(e) => e.preventDefault()}
			footer={
				<>
					<span className="hidden text-xs text-secondary sm:block">
						{summaryParts.length > 0
							? `Notifies ${summaryParts.join(" and ")}. You can undo for 10 seconds.`
							: "Shows in the widget only."}
					</span>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
					<span
						className="flex"
						onClick={linkBlocked ? explainBlockedLink : undefined}
					>
						<Button
							variant="default"
							Icon={PaperPlane}
							disabled={isSending || linkBlocked}
							isLoading={isSending}
							onClick={executeSend}
							className={cn({ "pointer-events-none": linkBlocked })}
						>
							{emailOn || slackOn ? "Publish and notify" : "Publish"}
						</Button>
					</span>
				</>
			}
		>
			<div className="mt-3 grid border-t border-gray-200 md:h-[min(42rem,calc(90dvh-9.5rem))] md:grid-cols-[24rem_minmax(0,1fr)] dark:border-gray-700/80">
				<div className="flex min-h-0 flex-col gap-6 overflow-y-auto border-gray-200 px-6 pb-6 pt-4 md:border-r dark:border-gray-700/80">
					<section className="flex flex-col">
						<SettingsSection title="Channels" className="mt-0 pb-1" />
						<div className="flex flex-col">
							<ChannelRow
								title="In-app widget"
								meta="Always on"
								action={
									<Tooltip label="Always on. Turn changelog notifications off in Widget settings.">
										{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
										<span className="flex" onClick={explainAlwaysOn}>
											<Switch
												name="notify-in-app"
												checked
												disabled
												switchClassName="pointer-events-none"
												variant="branded"
												size="small"
											/>
										</span>
									</Tooltip>
								}
							/>
							<ChannelRow
								title="Email"
								meta={
									<>
										<span className="truncate">
											{pluralize(subscriberCount, "subscriber", "subscribers")}
										</span>
										{canUseEmail && csvData.length > 1 && (
											<Tooltip label="Download emails as CSV">
												<span>
													<CSVLink
														data={csvData}
														filename="recipients-export.csv"
														onClick={() =>
															toast.success({
																title: "Recipients CSV exported",
															})
														}
													>
														<IconButton
															Icon={Download}
															variant="ghost"
															size="md"
														/>
													</CSVLink>
												</span>
											</Tooltip>
										)}
										<Tooltip label="View all recipients">
											<IconButton
												Icon={Users}
												variant="ghost"
												size="md"
												onClick={() => window.open("/people", "_blank")}
											/>
										</Tooltip>
									</>
								}
								action={
									canUseEmail ? (
										<Switch
											name="send-email"
											checked={sendEmail}
											onCheckedChange={setSendEmail}
											disabled={isSending}
											variant="branded"
											size="small"
										/>
									) : !atLeastProPlan ? (
										<UpgradeButton required={pro.andUp} />
									) : (
										<Button
											variant="outline"
											size="sm"
											onClick={() => void navigate({ to: "/settings/emails" })}
										>
											Set up emails
										</Button>
									)
								}
							/>
							<ChannelRow
								title="Slack"
								meta={
									slackChannelCount > 0 ? (
										<SlackBroadcastChannelsPopover channels={slackChannels}>
											<button
												type="button"
												className="rounded-sm underline decoration-gray-300 decoration-dotted underline-offset-4 hover:text-primary hover:decoration-gray-500 dark:decoration-gray-600"
											>
												{pluralize(slackChannelCount, "channel", "channels")}
											</button>
										</SlackBroadcastChannelsPopover>
									) : (
										"0 channels"
									)
								}
								action={
									canUseSlack ? (
										<Switch
											name="send-slack"
											checked={sendSlack}
											onCheckedChange={setSendSlack}
											disabled={isSending}
											variant="branded"
											size="small"
										/>
									) : !atLeastProPlan ? (
										<UpgradeButton required={pro.andUp} />
									) : (
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												void navigate({ to: "/settings/integrations/slack" })
											}
										>
											Set up Slack
										</Button>
									)
								}
							/>
						</div>
					</section>

					{emailOn && fromEmail && (
						<section className="flex flex-col">
							<SettingsSection title="Email" className="mt-0 pb-1" />
							<div className="flex flex-col">
								<FieldRow label="From">
									{fromOptions.length > 1 ? (
										<DropdownMenu
											contentProps={{ align: "start" }}
											trigger={
												<Button
													variant="outline"
													iconRight={ChevronDownIcon}
													className="w-full justify-between"
													disabled={isSending}
												>
													<span className="truncate">{fromEmail}</span>
												</Button>
											}
										>
											<DropdownMenuGroup>
												{fromOptions.map((option) => (
													<DropdownMenuItem
														key={option.email}
														IconRight={
															option.email === fromEmail ? CheckIcon : undefined
														}
														onSelect={() => setFromOverride(option.email)}
													>
														<span className="flex min-w-0 items-baseline gap-1.5">
															<span className="truncate">{option.email}</span>
															{option.isBroadcastDefault && (
																<span className="flex-none text-[11px] text-tertiary">
																	Default
																</span>
															)}
														</span>
													</DropdownMenuItem>
												))}
											</DropdownMenuGroup>
										</DropdownMenu>
									) : (
										<span className="truncate pt-1.5 text-sm">{fromEmail}</span>
									)}
								</FieldRow>
								<FieldRow label="Sender name" htmlFor="sender-name">
									<Input
										name="sender-name"
										value={senderName}
										onChange={(e) => setSenderName(e.target.value)}
										placeholder={defaultSenderName || "Workspace name"}
										maxLength={SENDER_NAME_MAX_LENGTH}
										disabled={isSending}
									/>
								</FieldRow>
								<FieldRow label="Subject" htmlFor="email-subject">
									<Input
										name="email-subject"
										value={subject}
										onChange={(e) => setSubject(e.target.value)}
										placeholder={`New: ${entity.name}`}
										maxLength={CHANGELOG_TITLE_MAXIMUM_LENGTH}
										disabled={isSending}
									/>
								</FieldRow>
							</div>
						</section>
					)}

					<section className="flex flex-col">
						<SettingsSection title="Read more button" className="mt-0 pb-2.5" />
						<SegmentedControl
							name="changelog-button-mode"
							value={buttonMode}
							options={BUTTON_MODES}
							onValueChange={setButtonMode}
							showLabels
							className="self-start"
						/>
						{buttonMode === "none" ? (
							<p className="mt-3 text-xs text-secondary">
								Emails and Slack posts end after the post text.
							</p>
						) : (
							<div className="mt-1.5 flex flex-col">
								<FieldRow label="Button text" htmlFor="changelog-button-label">
									<Input
										name="changelog-button-label"
										value={buttonLabel}
										onChange={(e) => setButtonLabel(e.target.value)}
										placeholder={DEFAULT_CHANGELOG_BUTTON_LABEL}
										maxLength={CHANGELOG_BUTTON_LABEL_MAX_LENGTH}
										disabled={isSending}
									/>
								</FieldRow>
								{buttonMode === "post" && preview?.postUrl && (
									<FieldRow label="Link" htmlFor="changelog-post-url">
										<PostLinkField url={preview.postUrl} />
									</FieldRow>
								)}
								{buttonMode === "custom" && (
									<FieldRow label="Link" htmlFor="changelog-button-url">
										<ButtonLinkField
											value={buttonUrl}
											onChange={setButtonUrl}
											disabled={isSending}
											ownDomains={ownDomains}
										/>
									</FieldRow>
								)}
							</div>
						)}
					</section>
				</div>

				<div className="flex min-h-[28rem] flex-col">
					<div className="flex h-12 flex-none items-center justify-between gap-3 border-b border-gray-200 px-4 dark:border-gray-700/80">
						{activeTab ? (
							<>
								<Tabs
									value={activeTab}
									onValueChange={(value) => setPreviewTab(value as PreviewTab)}
								>
									<TabsList aria-label="Preview">
										{tabs.map((tab) => (
											<TabsTrigger key={tab.value} value={tab.value}>
												{tab.label}
											</TabsTrigger>
										))}
									</TabsList>
								</Tabs>

								<Tooltip
									label={
										activeTab === "slack"
											? "Send test Slack post to your notification channel"
											: "Send test email to yourself"
									}
								>
									<Button
										variant="outline"
										size="sm"
										onClick={handleSendTest}
										disabled={isSending || isSendingTest}
										isLoading={isSendingTest}
									>
										{activeTab === "slack"
											? "Send test to Slack"
											: "Send test email"}
									</Button>
								</Tooltip>
							</>
						) : (
							<span className="text-xs text-secondary">Preview</span>
						)}
					</div>
					<div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5">
						{!activeTab ? (
							<p className="px-6 py-20 text-center text-sm text-secondary">
								Email and Slack are off. The post shows in the widget only.
							</p>
						) : !preview ? (
							<div className="flex justify-center py-20">
								{previewQuery.isError ? (
									<p className="text-sm text-secondary">
										{"The preview didn't load. Send a test to check the post."}
									</p>
								) : (
									<Loader />
								)}
							</div>
						) : activeTab === "email" ? (
							<div className="flex flex-col gap-4">
								<div className="flex items-start gap-3 border-b border-gray-200 pb-4 dark:border-gray-700/80">
									<span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gray-900 text-xs font-semibold text-white dark:bg-gray-100 dark:text-gray-900">
										{preview.email.fromName.charAt(0).toUpperCase()}
									</span>
									<div className="flex min-w-0 flex-1 flex-col gap-0.5">
										<div className="flex min-w-0 items-baseline gap-1.5">
											<span className="truncate text-sm font-semibold">
												{preview.email.fromName}
											</span>
											{preview.email.fromEmail && (
												<span className="truncate text-xs text-secondary">
													&lt;{preview.email.fromEmail}&gt;
												</span>
											)}
										</div>
										<span className="truncate text-sm font-medium">
											{preview.email.subject}
										</span>
										<span className="truncate text-xs text-secondary">
											{preview.email.previewText}
										</span>
									</div>
								</div>
								<div className="overflow-hidden rounded-lg">
									<EmailPreviewFrame html={preview.email.html} />
								</div>
							</div>
						) : (
							<SlackMessagePreview
								username={preview.slack.username}
								iconUrl={preview.slack.iconUrl}
								blocks={preview.slack.blocks}
								video={preview.slack.video}
							/>
						)}
					</div>
				</div>
			</div>
		</Dialog>
	)
}

// One line in every case: the field scrolls, the tooltip holds the whole link.
function PostLinkField({ url }: { url: string }) {
	return (
		<Tooltip label={url}>
			<Input
				readOnly
				name="changelog-post-url"
				value={url.replace(/^https:\/\//, "")}
				inputClassName="hover:text-secondary"
				icon={SquareArrowUpRight}
				iconPosition="right"
				iconOnClick={() => window.open(url, "_blank", "noopener")}
			/>
		</Tooltip>
	)
}

function ButtonLinkField({
	value,
	onChange,
	disabled,
	ownDomains,
}: {
	value: string
	onChange: (value: string) => void
	disabled: boolean
	ownDomains: readonly string[]
}) {
	const [touched, setTouched] = useState(false)
	const check = checkChangelogButtonUrl(value)
	const showError = !check.ok && check.problem !== "empty" && touched

	let note: ReactNode = (
		<span className="text-tertiary">{CHANGELOG_BUTTON_URL_MESSAGES.empty}</span>
	)
	if (check.ok && check.lookalike) {
		note = (
			<LinkNote tone="warning">
				<b>{check.host}</b> uses lookalike characters. Check it before you send.
			</LinkNote>
		)
	} else if (check.ok && isOwnChangelogDomain(check.host, ownDomains)) {
		note = (
			<LinkNote tone="ok">
				<b>{check.host}</b> is one of your domains.
			</LinkNote>
		)
	} else if (check.ok) {
		note = (
			<LinkNote tone="warning">
				<b>{check.host}</b>
				{" isn't one of your domains. Check it before you send."}
			</LinkNote>
		)
	} else if (showError) {
		note = null
	}

	return (
		<div className="flex flex-col gap-1.5">
			<Input
				name="changelog-button-url"
				type="url"
				inputMode="url"
				spellCheck={false}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				onBlur={() => setTouched(true)}
				placeholder="https://yoursite.com/changelog/post"
				disabled={disabled}
				errorMessage={
					showError ? CHANGELOG_BUTTON_URL_MESSAGES[check.problem] : undefined
				}
			/>
			{note && <div className="text-xs leading-4">{note}</div>}
		</div>
	)
}

function LinkNote({
	tone,
	children,
}: {
	tone: "ok" | "warning"
	children: ReactNode
}) {
	const Icon = tone === "ok" ? CircleCheck : TriangleWarning
	return (
		<span
			className={cn(
				"flex items-start gap-1.5",
				tone === "ok"
					? "text-green-700 dark:text-green-400"
					: "text-amber-700 dark:text-amber-400",
			)}
		>
			<Icon className="mt-px h-3.5 w-3.5 flex-none" aria-hidden />
			<span>{children}</span>
		</span>
	)
}
